// Sprint 38 — Information Architecture (Workflow / Pipeline — Option A).
//
// Single source of truth for the app's navigation tree. The shape mirrors
// what a future GET /api/me/nav endpoint will return. For now the tree is
// declared client-side and filtered against the active user's permissions
// using authUtils. When the backend endpoint lands, swap the export for a
// hook that calls the API; consumers (NavRail) won't need to change.
//
// Sprint 41 IA (Sales / Estimating / Operations / People / Financial) is
// superseded by Sales / Field / Money / Office. Dashboard stays the
// primary top entry. Settings stays in the utility footer. Lark is the
// AI brain, not a nav section. Items hidden pending future-functionality
// refresh live in the commented "Deferred entries" block at the bottom
// (see C-Back-End/docs/MASTER-TASK-LIST.md → Future Functionality).

import {
  LayoutDashboard,
  Users, UserPlus,
  FileText,
  Calculator,
  ScrollText, FileSignature,
  Briefcase,
  MessagesSquare, Mail, Handshake, Landmark,
  Award,
  FileBarChart,
  Megaphone,
  Package, Wrench, Table2,
  UserCog,
  CheckSquare,
  Settings, MessageSquarePlus, HelpCircle,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { PERMISSIONS } from '@/constants/permissions';
import { authUtils } from '@/utils/auth';
import { getFeatureFlag } from '@/lib/feature-flags';
import { canAccessBookkeeperWork } from '@/lib/bookkeeper-access';
import { isCompanyOwner } from '@/lib/owner-access';

export interface NavItem {
  label: string;
  url: string;
  icon: LucideIcon;
  /** Permission required to see this item. If absent, always visible. */
  permission?: string;
  /** Counter badge (e.g. unread count, pending items). Resolved by the
   *  rendering shell — the tree definition is static. */
  counterKey?: string;
  /** Append to the end of the route to mark "in this section" for the
   *  active-state check. Default is `startsWith(url)`. */
  matchPrefix?: string;
  /** When set, this check wins over `permission`. Used for bookkeeper work
   *  that Estimator must not see even with leftover PROCUREMENT.VIEW. */
  canAccess?: () => boolean;
}

export interface NavSection {
  label: string;
  items: NavItem[];
}

export interface NavTree {
  /** Top-of-rail single entries above the section list (Dashboard etc). */
  primary: NavItem[];
  sections: NavSection[];
  /** Bottom-of-rail entries (Feedback / Support / Logout). */
  utility: NavItem[];
}

// ── Static tree definition (Workflow / Pipeline IA) ─────────────────

function primaryNav(): NavItem[] {
  if (isCompanyOwner()) {
    return [{ label: 'Owner home', url: '/owner', icon: LayoutDashboard }];
  }
  return [
    { label: 'Dashboard', url: '/dashboard', icon: LayoutDashboard, permission: PERMISSIONS.DASHBOARD.VIEW },
  ];
}

const TREE: NavTree = {
  primary: [
    { label: 'Dashboard', url: '/dashboard', icon: LayoutDashboard, permission: PERMISSIONS.DASHBOARD.VIEW },
  ],
  sections: [
    {
      label: 'Sales',
      items: [
        { label: 'Leads', url: '/leads', icon: UserPlus, permission: PERMISSIONS.LEAD.VIEW },
        { label: 'Clients', url: '/clients', icon: Users, permission: PERMISSIONS.CLIENT.DETAILS_VIEW },
        { label: 'Intake Forms', url: '/intake-forms', icon: FileText, permission: PERMISSIONS.INTAKE.VIEW },
        // [NAV-SORT-1] Sale Happy Path rail order: Intake → Estimate →
        // Proposal → Contract. Estimates used to live under Estimating,
        // which split the pipeline across two sections.
        { label: 'Estimates', url: '/estimates', icon: Calculator, permission: PERMISSIONS.ESTIMATE.READ },
        // Note (Wave 6 — May 2026): Sales Pipeline was consolidated into
        // Clients (/sales-pipeline → /clients). The pipeline filter now uses
        // the client-level pipelineStatus (e.g. ?pipelineStatus=READY_TO_PRESENT).
        // Proposals returns here per the Sprint 41 IA spec.
        { label: 'Proposals', url: '/proposals', icon: ScrollText, permission: PERMISSIONS.PROPOSAL.VIEW },
        { label: 'Contracts', url: '/contracts', icon: FileSignature, permission: PERMISSIONS.CONTRACT.VIEW },
        // Marketing module v1 — flag-gated alongside its /marketing route.
        ...(getFeatureFlag('marketingModule')
          ? [{ label: 'Marketing', url: '/marketing', icon: Megaphone, permission: PERMISSIONS.MARKETING.VIEW }]
          : []),
        { label: 'Products', url: '/catalog/products', icon: Package, permission: PERMISSIONS.PRODUCT.VIEW },
        // ProdTRed nav retired — governance [MPT enrichment] retires the
        // prodtred stack in favor of catalog-import / Products. Route redirects
        // to /catalog/products so old bookmarks do not 404.
        { label: 'Services', url: '/catalog/services', icon: Wrench, permission: PERMISSIONS.SERVICE.VIEW },
      ],
    },
    {
      label: 'Field',
      items: [
        { label: 'Projects', url: '/project-hub/projects', icon: Briefcase, permission: PERMISSIONS.PROJECT.VIEW },
        // Tasks v2 Phase 1 — Slice 1.4. Slots into the position the legacy
        // "Tasks" (now Project Fulfillment) used to occupy. Permission gated
        // on TASK.VIEW_OWN so non-task users (e.g. CLIENT_PORTAL) don't see
        // the entry. See docs/design-proposals/2026-05-22-tasks-v2-and-weekly-report.md §6.2.
        { label: 'Tasks', url: '/tasks', icon: CheckSquare, permission: PERMISSIONS.TASK.VIEW_OWN, matchPrefix: '/tasks' },
      ],
    },
    {
      // Money (was Financial) — Transactions is the only payable home.
      // Rewards and Pricing tables sit here with Transactions (nav lock).
      // Procurement folds into the Transactions row (invoice detail).
      // Do not re-add a Procurement sibling.
      label: 'Money',
      items: [
        { label: 'Transactions', url: '/financial/transactions', icon: Landmark, canAccess: canAccessBookkeeperWork, matchPrefix: '/financial' },
        { label: 'Rewards', url: '/rewards', icon: Award, permission: 'perm-rewards-view' },
        { label: 'Pricing tables', url: '/catalog/pricing-tables', icon: Table2, permission: PERMISSIONS.PRICING_TABLES.VIEW },
      ],
    },
    {
      label: 'Office',
      items: [
        { label: 'Activity Hub', url: '/activity-hub/company-chat', icon: MessagesSquare, permission: PERMISSIONS.ACTIVITY_HUB.VIEW, matchPrefix: '/activity-hub/company-chat', counterKey: 'chat' },
        { label: 'Correspondence', url: '/correspondence', icon: Mail, permission: PERMISSIONS.CORRESPONDENCE.VIEW },
        { label: 'Reports', url: '/reports', icon: FileBarChart, permission: PERMISSIONS.REPORT.VIEW },
        { label: 'Colleagues', url: '/teams', icon: UserCog, permission: PERMISSIONS.TEAM.VIEW },
        { label: 'Partners', url: '/partners', icon: Handshake, permission: PERMISSIONS.PARTNER.VIEW },
      ],
    },
  ],
  utility: [
    // Settings is also accessible from the top-right user-name dropdown,
    // but lives on the left rail too for one-click access.
    // Feedback opens a modal — wired in NavRail via onOpenFeedback.
    // Help is an external link placeholder; rendered in NavRail directly.
    { label: 'Settings', url: '/settings', icon: Settings },
  ],
};

// ── Deferred entries (kept here for future re-enable) ──────────────
//
// These items were on the rail before the May 2026 IA reshuffle. The
// pages are still reachable by URL — we just dropped the rail rows.
// To re-enable an item, uncomment its line, add the corresponding icon
// to the lucide-react import at the top, and drop the entry into the
// section you want it in.
//
// import { ClipboardList, DollarSign, TrendingUp, User as UserIcon, Wrench as ServiceIcon } from 'lucide-react';
//
// { label: 'Intake Queue',     url: '/intake-form/pending',           icon: ClipboardList, permission: PERMISSIONS.ESTIMATE.MODIFY },
// { label: 'Service Requests', url: '/service-requests',              icon: ServiceIcon,   permission: PERMISSIONS.SERVICE_REQUEST.VIEW },
// Cost Viewer analytics retired [PERM-EVAL-4].
// { label: 'Calc Insights',    url: '/insights/calculation-insights', icon: TrendingUp,    permission: PERMISSIONS.ESTIMATE.READ },
// { label: 'Catalog Overview', url: '/catalog/overview',              icon: TrendingUp,    permission: PERMISSIONS.PRODUCT.EDIT },
// { label: 'Profile',          url: '/profile',                       icon: UserIcon },

// ── Permission resolution ──────────────────────────────────────────

function hasAccess(item: NavItem): boolean {
  if (item.canAccess) return item.canAccess();
  if (!item.permission) {
    // Settings / Profile / counters are always shown to authenticated users
    if (item.url === '/settings') {
      const perms = authUtils.getUserPermissions();
      return perms.some((p) => p.includes('settings'));
    }
    return true;
  }
  return authUtils.hasPermission(item.permission);
}

/**
 * Returns the nav tree filtered down to what the current user is allowed
 * to see. Sections with zero visible items are dropped entirely.
 */
export function getFilteredNavTree(): NavTree {
  const primary = primaryNav().filter(hasAccess);
  const sections = TREE.sections
    .map((s) => ({ ...s, items: s.items.filter(hasAccess) }))
    .filter((s) => s.items.length > 0);
  const utility = TREE.utility.filter(hasAccess);
  return { primary, sections, utility };
}

/**
 * Returns true if the given current pathname falls under the item's
 * matched URL space. Used by NavItem to render the active accent.
 */
export function isItemActive(item: NavItem, pathname: string): boolean {
  const prefix = item.matchPrefix ?? item.url;
  return pathname === prefix || pathname.startsWith(prefix + '/');
}

export { TREE as NAV_TREE_RAW };

// Re-export utility icons for callers
export {
  MessageSquarePlus as FeedbackIcon,
  HelpCircle as HelpIcon,
};
